import type { ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";
import type { Unsubscribe } from "firebase/firestore";

import { guardNoryBatchWrite } from "@dashboard/domain/partner-managed";
import { Product } from "@dashboard/types/product";
import type { ReduxState } from "@dashboard/types/reduxState";
import type { ThunkExtraArgument } from "@dashboard/types/thunk";

import type { ErrorReducerAction } from "../reducers/errorReducer";
import { ProductReducerAction } from "../reducers/productReducer";
import { addError } from "./addErrorAction";
import { fetchResolvedEdibles } from "./resolvedEdibleActions";

type ThunkActionReturn = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  ProductReducerAction
>;

type ProductSaveThunkAction = ThunkAction<
  Promise<boolean>,
  ReduxState,
  ThunkExtraArgument,
  ProductReducerAction
>;

export type ProductThunkDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  ProductReducerAction | ErrorReducerAction
>;

export const setProduct: (
  product: Product,
  isNew: boolean
) => ProductSaveThunkAction = (product: Product, isNew: boolean) => {
  return (
    dispatch: ProductThunkDispatch,
    getState: () => ReduxState,
    { productApi }: ThunkExtraArgument
  ) => {
    const stored = getState().product.products.find(
      (candidate) => candidate.id === product.id
    );
    const guarded = guardNoryBatchWrite(product, stored);
    return productApi
      .setProduct(guarded, isNew)
      .then(() => true)
      .catch((error) => {
        addError(dispatch)(error);
        return false;
      });
  };
};

export const deleteProduct: (
  restaurantUuid: string,
  productId: string
) => ThunkActionReturn = (restaurantUuid: string, productId: string) => {
  return (
    dispatch: ProductThunkDispatch,
    getState: () => ReduxState,
    { productApi }: ThunkExtraArgument
  ) => {
    return productApi
      .deleteProduct(restaurantUuid, productId)
      .catch(addError(dispatch));
  };
};

// Deletes multiple products at once
export const bulkDeleteProducts: (
  restaurantUuid: string,
  productIds: string[]
) => ThunkActionReturn = (restaurantUuid: string, productIds: string[]) => {
  return (
    dispatch: ProductThunkDispatch,
    getState: () => ReduxState,
    { productApi }: ThunkExtraArgument
  ) => {
    return productApi
      .bulkDeleteProducts(restaurantUuid, productIds)
      .catch(addError(dispatch));
  };
};

// Live listener: returns the unsubscribe synchronously (not a promise).
export const subscribeToProducts =
  (
    restaurantUuid: string
  ): ThunkAction<
    Unsubscribe,
    ReduxState,
    ThunkExtraArgument,
    ProductReducerAction
  > =>
  (dispatch, getState, { productApi }) =>
    productApi.subscribeToProducts(
      restaurantUuid,
      (payload) => {
        if (getState().product.initialProductsLoading) {
          dispatch({ type: "FETCHED_PRODUCTS", payload });
          return;
        }
        // Refresh resolved edibles after each change, since that data is now
        // stale. The resolvedEdibles state must be updated before the changed
        // products are put into state, so that every UI component finds a
        // product it renders in the resolvedEdibles state.
        dispatch(fetchResolvedEdibles()).then(() =>
          dispatch({ type: "FETCHED_PRODUCTS", payload })
        );
      },
      addError(dispatch)
    );

export const fetchProductsForRestaurant = (
  restaurantUuid: string
): ThunkAction<
  Promise<Product[]>,
  ReduxState,
  ThunkExtraArgument,
  ProductReducerAction
> => {
  return (_dispatch, _getState, { productApi }) => {
    return productApi.fetchProducts(restaurantUuid);
  };
};
